// import libraries
import React, { useEffect, useState } from "react";
import {
  useSearchParams,
  useParams,
  useLocation,
  Link,
  useNavigate,
} from "react-router-dom";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";

// import components
import { TabMenu } from "primereact/tabmenu";
import { Menubar } from "primereact/menubar";

// import hooks
import { useKeyboardVisibility } from "../../hooks/useKeyboardVisibility";

// import utils
import { firebase } from "../../services/firebase";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { logError, logEvent } from "../../utils/analytics";

// import screens
import Chat from "../../screens/public/Chat/Index";
import ShopDetails from "../../screens/public/ShopDetails/ShopDetails";
import NotFound from "../../screens/public/NotFound/Index";

// import styling
import "./style.css";

const PublicNavigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isAuthed, setIsAuthed] = useState(!!getAuth().currentUser);

  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, (user) => {
      setIsAuthed(!!user);
    });
    return () => unsub();
  }, []);

  const items = [
    { label: "Home", icon: "pi pi-home", url: isAuthed ? "/dashboard" : "/" },
    { label: "Map", icon: "pi pi-map", url: "/map" },
    isAuthed
      ? {
          label: "Logout",
          icon: "pi pi-sign-out",
          onClick: async () => {
            const auth = getAuth();
            try {
              await signOut(auth);
            } finally {
              navigate("/login", { replace: true });
            }
          },
        }
      : { label: "Login", icon: "pi pi-user", url: "/login" },
  ];

  const start = (
    <div className="flex align-items-center">
      <Link to="/" className="no-underline">
        <span className="text-900 font-bold text-xl ml-2">Yes Boss</span>
      </Link>
    </div>
  );

  return (
    <div className="top-wrapper">
      <div className="card">
        <Menubar
          model={items.map((item) => ({
            ...item,
            className:
              item.url && location.pathname === item.url
                ? "p-menuitem-active"
                : "",
            template: (menuItem) =>
              menuItem.onClick ? (
                <button
                  type="button"
                  className="p-menuitem-link"
                  onClick={menuItem.onClick}
                >
                  <span className={menuItem.icon}></span>
                  <span className="ml-2">{menuItem.label}</span>
                </button>
              ) : (
                <Link to={menuItem.url} className="p-menuitem-link">
                  <span className={menuItem.icon}></span>
                  <span className="ml-2">{menuItem.label}</span>
                </Link>
              ),
          }))}
          start={start}
          className="border-none shadow-2"
        />
      </div>
    </div>
  );
};

export const PublicLayout = ({ children }) => {
  const [searchParams] = useSearchParams();
  const { businessName: urlShopName } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [isRedirecting, setIsRedirecting] = useState(false);

  const hasShopContext = Boolean(urlShopName);

  // Handle chat?user={userId} URLs
  useEffect(() => {
    const userId = searchParams.get("user");

    if (location.pathname === "/chat" && userId && !isRedirecting) {
      const fetchBusinessDetails = async () => {
        try {
          setIsRedirecting(true);
          const businessRef = doc(firebase, "businessAtAGlance", userId);
          const businessSnap = await getDoc(businessRef);

          if (businessSnap.exists()) {
            const businessData = businessSnap.data();
            const shopName = businessData.shopName || businessData.businessName;

            if (shopName) {
              // Clean and format the shop name for URL (keep spaces)
              const formattedShopName = shopName
                .toLowerCase()
                .replace(/[^\w\s-]/g, "")
                .replace(/\s+/g, " ")
                .trim();

              // Preserve any other query parameters
              const searchParams = new URLSearchParams(location.search);
              searchParams.delete("user");
              const queryString = searchParams.toString();

              // Redirect to the proper URL
              navigate(
                `/${formattedShopName}${queryString ? `?${queryString}` : ""}`,
                { replace: true }
              );
              return;
            }
          }

          // If we get here, the business wasn't found or had no name
          navigate("/not-found", { replace: true });
        } catch (error) {
          console.error("Error fetching business details:", error);
          navigate("/error", { replace: true });
        } finally {
          setIsRedirecting(false);
        }
      };

      fetchBusinessDetails();
    }
  }, [location.search, location.pathname, navigate, isRedirecting]);

  const [shopDetails, setShopDetails] = useState(null);
  const [shopFeatures, setShopFeatures] = useState({});
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const isKeyboardOpen = useKeyboardVisibility();
  const [userId, setUserId] = useState(searchParams.get("user"));
  const [showFloatingChat, setShowFloatingChat] = useState(false);

  const isChatRoute = location.pathname.split("/").includes("chat");

  const mobileTabItems = [
    { label: "Shop Detail", icon: "pi pi-shopping-bag" },
    { label: "Chat", icon: "pi pi-comments" },
  ];

  useEffect(() => {
    if (!hasShopContext) {
      setLoading(false); // Hide loader if no shop context is needed
      return;
    }

    let fetchAttempts = 0;
    const maxAttempts = 2;

    // Reset loading state when route changes
    setLoading(true);

    const fetchAllBusinesses = async () => {
      try {
        const querySnapshot = await getDocs(
          collection(firebase, "businessAtAGlance")
        );
        const businesses = [];
        querySnapshot.forEach((doc) => {
          businesses.push({ id: doc.id, ...doc.data() });
        });
        return businesses;
      } catch (error) {
        console.error("Error fetching all businesses:", error);
        return [];
      }
    };

    // Create a URL-friendly version of the shop name that preserves spaces
    const createShopUrl = (str) => {
      if (!str) return '';
      return str
        .toString()
        .trim()
        // Replace multiple spaces with a single space
        .replace(/\s+/g, ' ')
        // Remove characters that aren't letters, numbers, or spaces
        .replace(/[^a-z0-9\s]/gi, '')
        // Trim any leading/trailing spaces
        .trim();
    };

    const fetchUserIdByShopName = async () => {
      if (!urlShopName) return null;
      try {
        const allBusinesses = await fetchAllBusinesses();
        
        // First try exact match
        const exactMatch = allBusinesses.find(business => 
          business.shopName && 
          business.shopName.toLowerCase() === urlShopName.toLowerCase()
        );
        
        if (exactMatch) return exactMatch.uid;

        // If no exact match, try URL-friendly match
        const normalizedSearch = createShopUrl(urlShopName);
        if (!normalizedSearch) return null;

        const matchedBusiness = allBusinesses.find((business) => {
          if (!business.shopName) return false;
          
          // Try direct match first for better performance
          if (business.shopName.toLowerCase() === urlShopName.toLowerCase()) {
            return true;
          }
          
          // Then try URL-friendly match
          const businessUrlName = createShopUrl(business.shopName);
          const searchUrlName = createShopUrl(urlShopName);
          return businessUrlName.toLowerCase() === searchUrlName.toLowerCase();
        });
        
        return matchedBusiness ? matchedBusiness.uid : null;
      } catch (error) {
        console.error("Error finding user by shop name:", error);
        return null;
      }
    };

    const fetchShopDetails = async () => {
      let targetUserId = userId;
      let foundShopDetails = null;

      if (!targetUserId && urlShopName) {
        // First try to get the user ID from the shop name
        targetUserId = await fetchUserIdByShopName();
        
        if (!targetUserId) {
          // If we still don't have a user ID, try to find the business directly
          const allBusinesses = await fetchAllBusinesses();
          const matchedBusiness = allBusinesses.find(business => 
            business.shopName && 
            business.shopName.toLowerCase() === urlShopName.toLowerCase()
          );
          
          if (matchedBusiness) {
            targetUserId = matchedBusiness.uid;
            foundShopDetails = matchedBusiness;
          } else {
            setLoading(false);
            return;
          }
        }
        
        setUserId(targetUserId);
      }

      if ((!targetUserId && !urlShopName) || fetchAttempts >= maxAttempts) {
        setLoading(false);
        return;
      }

      try {
        const userRef = doc(firebase, "users", targetUserId);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          const data = userSnap.data();
          setShopDetails({ id: targetUserId, ...data });

          logEvent("shop_details_viewed", {
            shop_id: targetUserId,
            shop_name: data.shopName,
            shop_type: data.shopType,
          });
        }

        const businessesRef = doc(firebase, "businesses", targetUserId);
        const businessesSnap = await getDoc(businessesRef);

        if (businessesSnap.exists()) {
          const data = businessesSnap.data();
          const formattedFeatures = {};
          const userShopTypes = userSnap.data()?.shopType || [];

          Object.entries(data.shopType).forEach(([category, items]) => {
            if (
              category === "id" ||
              category === "updatedAt" ||
              !Array.isArray(items)
            )
              return;

            const formattedItems = [];
            items.forEach((item) => {
              const [shopTypeKey, shopTypeData] = Object.entries(item)[0] || [];
              if (shopTypeKey && userShopTypes.includes(shopTypeKey)) {
                const subCategory = shopTypeKey.includes("_")
                  ? shopTypeKey.split("_").slice(1).join("_")
                  : "";
                formattedItems.push({
                  type: shopTypeKey,
                  subCategory:
                    subCategory.charAt(0).toUpperCase() + subCategory.slice(1),
                  ...shopTypeData,
                });
              }
            });

            if (formattedItems.length > 0) {
              formattedFeatures[category] = formattedItems;
            }
          });
          setShopFeatures(formattedFeatures);

          try {
            const articlesQuery = query(
              collection(firebase, "articles"),
              where("authorId", "==", targetUserId)
            );
            const articlesSnapshot = await getDocs(articlesQuery);
            const articlesData = articlesSnapshot.docs
              .map((doc) => ({ id: doc.id, ...doc.data() }))
              .filter((article) => article.status === "PUBLISHED");
            setArticles(articlesData);
          } catch (error) {
            logError("fetch_articles", error, { userId: targetUserId });
          }

          logEvent("shop_details_viewed", {
            shop_id: targetUserId,
            shop_name: userSnap.data()?.shopName,
            shop_type: userSnap.data()?.shopType,
          });
        }
      } catch (error) {
        logError("PublicLayout", error, { userId: targetUserId });
      } finally {
        // If we found shop details directly from the businesses collection, use them
        if (foundShopDetails && !shopDetails) {
          setShopDetails({
            id: targetUserId,
            ...foundShopDetails,
            // Make sure we have the storeImage in the shop details
            storeImage: foundShopDetails.storeImage || shopDetails?.storeImage
          });
        }
      }

      fetchAttempts++;
      if (
        fetchAttempts < maxAttempts &&
        (!shopDetails || !Object.keys(shopFeatures).length || !articles.length)
      ) {
        setTimeout(() => fetchShopDetails(), 1000);
      } else {
        setLoading(false);
      }
    };

    fetchShopDetails();
  }, [urlShopName, userId, hasShopContext]); // Add dependencies to re-run effect when these change

  // Add effect to handle loader state during navigation
  useEffect(() => {
    const unlisten = () => {
      // Hide loader when navigation starts
      setLoading(false);
    };

    // Set up navigation listener
    const unlistenFunc = window.addEventListener("popstate", unlisten);

    // Clean up listener on unmount
    return () => {
      window.removeEventListener("popstate", unlisten);
    };
  }, []);

  if (isRedirecting) {
    return (
      <div className="public-layout">
        <PublicNavigation />
        <div className="content-container">
          <div
            className="p-d-flex p-jc-center p-ai-center"
            style={{ height: "60vh" }}
          >
            <i
              className="pi pi-spin pi-spinner"
              style={{ fontSize: "2rem" }}
            ></i>
            <span className="ml-2">Loading business information...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`public-layout ${isKeyboardOpen ? "keyboard-open" : ""}`}>
      <PublicNavigation />
      <div className="content-wrapper">
        {hasShopContext ? (
          <div className={`content ${isChatRoute ? "chat-route" : ""}`}>
            {!isChatRoute && <ShopDetails shopDetails={shopDetails} />}
            {isChatRoute && (
              <div className="mobile-chat-fullheight">
                <Chat shopDetails={shopDetails} userId={userId} />
              </div>
            )}
          </div>
        ) : (
          <div className="content">{children}</div>
        )}
        {hasShopContext && !isChatRoute && (
          <div className="hidden md:block">
            <button
              type="button"
              className={
                "floating-chat-toggle " + (showFloatingChat ? "open" : "close")
              }
              onClick={() => setShowFloatingChat((v) => !v)}
              aria-expanded={showFloatingChat}
              aria-controls="floating-chat-panel"
            >
              {showFloatingChat ? "Close Chat" : "Open Chat"}
            </button>

            {/* Panel */}
            {showFloatingChat && (
              <div id="floating-chat-panel" className="floating-chat-container">
                <div className="floating-chat-body">
                  <Chat shopDetails={shopDetails} userId={userId} />
                </div>
              </div>
            )}
          </div>
        )}
        {/* Mobile Bottom Wrapper: Tab Menu for Shop Detail and Chat */}
        {hasShopContext && (
          <div className="md:hidden tab-menu-container">
            <TabMenu
              model={mobileTabItems}
              activeIndex={isChatRoute ? 1 : 0}
              onTabChange={(e) => {
                if (e.index === 1) {
                  navigate(
                    `/${urlShopName}/chat${userId ? `?user=${userId}` : ""}`
                  );
                } else {
                  navigate(`/${urlShopName}${userId ? `?user=${userId}` : ""}`);
                }
              }}
              className="w-full bg-white"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicLayout;
